import React, {createContext, ReactNode, useContext, useState, useCallback} from 'react';

interface RightSidebarContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  content: ReactNode | null;
  setContent: (content: ReactNode | null, title?: string | ReactNode, actions?: ReactNode) => void;
  setTitle: (title: string | ReactNode) => void;
  title: string | ReactNode;
  actions: ReactNode | null;
  toggle: () => void;
}

const RightSidebarContext = createContext<RightSidebarContextType | undefined>(undefined);

export function RightSidebarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent_] = useState<ReactNode | null>(null);
  const [title, setTitle] = useState<string | ReactNode>('Details');
  const [actions, setActions] = useState<ReactNode | null>(null);

  const setContent = useCallback((content: ReactNode | null, newTitle?: string | ReactNode, newActions?: ReactNode) => {
    setContent_(content);
    if (newTitle !== undefined) {
      setTitle(newTitle);
    } else if (content === null) {
      // Reset title when content is cleared
      setTitle('Details');
    }

    if (newActions !== undefined) {
      setActions(newActions);
    } else if (content === null) {
      // Reset actions when content is cleared
      setActions(null);
    }
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  return (
    <RightSidebarContext.Provider value={{ isOpen, setIsOpen, content, setContent, setTitle, title, actions, toggle }}>
      {children}
    </RightSidebarContext.Provider>
  );
}

export function useRightSidebar() {
  const context = useContext(RightSidebarContext);
  if (context === undefined) {
    throw new Error('useRightSidebar must be used within a RightSidebarProvider');
  }
  return context;
}